/**
 * Schwebender Lade-Hinweis (Spinner-Popup) - Auftrag "Schwebender Lade-Hinweis (Spinner-Popup)
 * statt Textzeile" (2026-08-28).
 *
 * ================= Vorgeschichte =================
 *
 * Der Auftrag "Serverseitiger Archivfilter + Ladeanzeige" (2026-08-27) hatte den Ladehinweis als
 * EINE Textzeile (`.empty-note`) in `Board/BoardSeite.tsx` untergebracht, dort NUR sichtbar, wenn
 * `ladend && bestellungen.length === 0` war - also beim Erstladen, aber NICHT bei einem spaeteren
 * Archivfilter-Wechsel (das Board steht dann bereits voll vom vorherigen Filterwert, ohne jeden
 * sichtbaren Hinweis auf den laufenden Neuladelauf). Nutzerfeedback: unsichtbar beim Filterwechsel,
 * und selbst beim Erstladen "eine nackte, zentrierte Zeile, die Platz einnimmt/wieder freigibt".
 *
 * Diese Komponente ersetzt jene Textzeile VOLLSTAENDIG (die alte Bedingung samt Kommentar ist aus
 * `BoardSeite.tsx` entfernt, die Prop `ladend` dort ersatzlos gestrichen) und sitzt bewusst NICHT
 * mehr board-lokal, sondern auf App-Ebene (`App.tsx`, `<LadeHinweis sichtbar={zustand.ladend} />`,
 * neben `<Toast text={meldung} />`): `zustand.ladend` ist GLOBALER Store-Zustand, nicht auf das
 * Board beschraenkt - die Rolle `payment_manager` laedt beim Anmelden ueber DENSELBEN Zustand
 * (immer `archiv=all`), unabhaengig davon, welche Seite gerade sichtbar ist. Ein weiterhin
 * board-lokaler Hinweis waere fuer einen reinen Zahlungsansicht-Nutzer nie sichtbar gewesen.
 *
 * ================= Visuell an Toast.tsx angelehnt, aber EIGENSTAENDIG =================
 *
 * Dieselbe Grundform wie `#toast` (`styles/bestand.css:2474-2480`) - dunkle Pille, unten mittig,
 * slide-up-Uebergang, dark-mode-fest, `pointer-events: none` (blockiert keine Bedienung darunter) -
 * ABER eine EIGENE Id (`#lade-hinweis`), keine Kollision mit `#toast`: die Ansteuerung ist
 * grundverschieden. `Toast`/`useMeldungsAnzeige` ist an einen festen 2200ms-Timer gekoppelt (Text
 * erscheint, verschwindet nach fester Dauer von selbst) - dieser Hinweis hat KEINEN Timer, er ist
 * exakt so lange sichtbar, wie `zustand.ladend` wahr ist (siehe `state/Speicher.tsx`:
 * `bestandLaden()`/`bestandNeuLaden()` setzen/loeschen ihn ueber die volle Laufzeit des jeweiligen
 * Ladelaufs, Erstladen UND jeder spaetere Archivfilter-Wechsel gleichermassen).
 *
 * Das CSS steht NICHT in `bestand.css`, sondern eigenstaendig in `styles/neubau/ladeHinweis.css`
 * (importiert ueber `main.tsx`): `bestand.css` ist eine woertliche, zeichengleiche Uebernahme des
 * `<style>`-Blocks aus `index.html` (`test/reference/stylesheet.test.ts`) - eine neue Regel dort
 * haette diesen Test rot gemacht. Der Spinner selbst ist keine Neuerfindung:
 * `.lade-hinweis-spinner` nutzt DIESELBE `@keyframes extrasDreht` wie `.supplier-extras-laedt`
 * (`styles/neubau/lieferanten.css`) - beide Dateien werden ueber `main.tsx` in denselben Bundle
 * geladen, Keyframe-Namen sind global.
 *
 * ================= Randfall: Ueberschneidung mit Toast =================
 *
 * `Toast` und dieser Hinweis koennten selten gleichzeitig sichtbar sein (z. B. eine
 * Archivieren-Bestaetigung waehrend im Hintergrund noch geladen wird) - beide sitzen an derselben
 * horizontalen Position (unten mittig). Entzerrt ueber einen leichten vertikalen Versatz im CSS
 * (`#lade-hinweis` sitzt hoeher als `#toast`, siehe `styles/neubau/ladeHinweis.css`) - keine
 * weitere Logik noetig.
 */
export interface LadeHinweisProps {
  /** `zustand.ladend` - `true` waehrend `bestandLaden()`/`bestandNeuLaden()` unterwegs sind. */
  sichtbar: boolean;
}

export function LadeHinweis({ sichtbar }: LadeHinweisProps) {
  return (
    <div id="lade-hinweis" className={sichtbar ? "show" : ""} role="status">
      {/*
       * Nachbesserung (Pruefbericht, 2026-08-28, Befund "Wichtig"): Spinner UND Text werden NUR NOCH
       * gerendert, wenn `sichtbar` wahr ist - vorher standen beide dauerhaft im DOM, nur die Klasse
       * wechselte, analog zu `#toast` (`Toast.tsx:24-26`, `{text}` nur bei gesetztem Text). Zwei
       * Folgen der alten Fassung waren so nicht gewollt: (1) der Text war ausserhalb des
       * `.show`-Zustands trotzdem lesbar/im Accessibility-Baum vorhanden (siehe Befund "Kritisch"
       * unten), und (2) `role="status"` meldete NIE etwas, weil sich der Inhalt der Live-Region nie
       * AENDERTE - Screenreader kuendigen einen Ladebeginn nur an, wenn innerhalb einer bereits
       * gemounteten Live-Region tatsaechlich neuer Inhalt erscheint. Die aeussere `div#lade-hinweis`
       * selbst bleibt UNBEDINGT gemountet (nicht `sichtbar && <div>...</div>`) - notwendig, aber
       * ALLEIN NICHT hinreichend fuer eine funktionierende Live-Region: `styles/neubau/
       * ladeHinweis.css` darf im Ruhezustand `visibility: hidden` NICHT verwenden (Nachbesserung
       * Runde 2, siehe dortiger Kopfkommentar) - das naehme die Region trotz unbedingtem Mounten aus
       * dem Barrierefreiheitsbaum, Region und Inhalt entstuenden dann im selben Schritt, keine
       * Ansage. `opacity: 0` allein haelt die Region im Baum und ist visuell gleichwertig.
       *
       * `aria-hidden` auf dem Spinner bleibt bestehen, damit der zugaengliche Name des
       * `role="status"`-Elements nur aus dem Text besteht - dasselbe Muster wie
       * `.supplier-extras-laedt` in `ExtrasFormular.tsx`.
       */}
      {sichtbar && (
        <>
          <span className="lade-hinweis-spinner" aria-hidden="true" />
          Bestellungen werden geladen…
        </>
      )}
    </div>
  );
}
